import { useRef, useState } from 'react'
import type { Popover as BasePopover } from '@base-ui/react/popover'

import { useChanged } from './use-changed'

type CalendarPopover = {
  open: boolean
  setOpen: (open: boolean) => void
  /** Element the panel positions against, and the region a click counts as "inside". */
  anchorRef: React.RefObject<HTMLDivElement | null>
  popupRef: React.RefObject<HTMLDivElement | null>
  /** Month the calendar shows, following `anchorDate` whenever it changes. */
  month: Date | undefined
  setMonth: (month: Date) => void
  rootProps: {
    open: boolean
    onOpenChange: (open: boolean, details: BasePopover.Root.ChangeEventDetails) => void
  }
  anchorProps: {
    ref: React.RefObject<HTMLDivElement | null>
    onBlur: (event: React.FocusEvent<HTMLElement>) => void
    onFocusCapture: (event: React.FocusEvent<HTMLElement>) => void
  }
}

/**
 * The shared shell behind `DatePicker` and `DateRangePicker`: a Base UI popover
 * anchored to a row of masked fields, plus the visible month.
 *
 * Both components need identical, non-obvious dismissal handling, and having it
 * in two places meant both browser-only bugs it exists to prevent had to be
 * found and fixed twice:
 *
 *   - The popup is portalled, so focus moving into it leaves the anchor. Testing
 *     containment against the anchor alone closed the panel on the mousedown that
 *     precedes every click inside it, swallowing the selection entirely.
 *   - The fields sit outside `Popover.Trigger` (only the calendar button is the
 *     trigger), so Base UI reads a click on a field as an outside press and
 *     dismisses — while the field's own focus handler is opening the panel. The
 *     two race and the calendar flashes open and shut.
 *
 * Opening on focus is restricted to inputs: including the trigger would make it
 * fight its own toggle.
 */
export function useCalendarPopover({
  anchorDate,
  disabled,
}: {
  anchorDate: Date | null
  disabled?: boolean
}): CalendarPopover {
  const anchorRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState<Date | undefined>(anchorDate ?? undefined)

  // Controlled, so a preset or a typed date moves the calendar to it instead of
  // leaving the selection out of view; `defaultMonth` is only read on mount.
  if (useChanged(anchorDate?.getTime() ?? null) && anchorDate) {
    setMonth(anchorDate)
  }

  const containsTarget = (target: EventTarget | null) =>
    target instanceof Node &&
    (anchorRef.current?.contains(target) === true || popupRef.current?.contains(target) === true)

  return {
    open,
    setOpen,
    anchorRef,
    popupRef,
    month,
    setMonth,
    rootProps: {
      open,
      onOpenChange: (nextOpen, details) => {
        if (
          !nextOpen &&
          details.reason === 'outside-press' &&
          containsTarget(details.event.target)
        ) {
          return
        }

        setOpen(nextOpen)
      },
    },
    anchorProps: {
      ref: anchorRef,
      onBlur: (event) => {
        // Base UI dismisses on Escape and outside-press but not on focus-out.
        if (!containsTarget(event.relatedTarget)) {
          setOpen(false)
        }
      },
      onFocusCapture: (event) => {
        if (disabled !== true && event.target instanceof HTMLInputElement) {
          setOpen(true)
        }
      },
    },
  }
}
